import { createElement, type ChangeEvent, type ReactElement } from 'react';
import { ArrowDropDown } from '@mui/icons-material';
import CustomDropdown from '../../../components/common/CustomDropdown';
import { BankTransactionIncomeExpenseTypeEditCell } from '../../bank-transactions/components/BankTransactionIncomeExpenseTypeEditCell';
import { FileCell } from '../components/data-display/cells/FileCell';
import { LookupGridDropdown } from '../components/data-display/LookupGridDropdown';
import type {
  GridEditCellTemplateContext,
  GridEditCellTemplateRegistry,
  GridEditCellTemplateRenderer,
} from './types';

const hasLookupMap = (
  lookupMap: GridEditCellTemplateContext['lookupMap']
): lookupMap is Record<string, string> => lookupMap !== undefined;

const hasLookupGridConfiguration = (context: GridEditCellTemplateContext): boolean => {
  const gridId = context.columnMetadata.additionalData?.gridId;
  const relatedColumns = context.columnMetadata.additionalData?.relatedColumns;

  return (
    typeof gridId === 'string' &&
    gridId.trim().length > 0 &&
    typeof relatedColumns === 'string' &&
    relatedColumns.trim().length > 0
  );
};

export const isValidColor = (value: unknown): value is string =>
  /^#[0-9a-f]{6}$/i.test(String(value ?? ''));

export const normalizeColor = (value: unknown): string =>
  isValidColor(value) ? String(value) : '#ffffff';

const selectLookupRenderer: GridEditCellTemplateRenderer = ({
  value,
  lookupMap,
  displayLookupMap,
  isInvalidLookupValue,
  onValueChange,
}) => {
  if (!hasLookupMap(lookupMap)) return null;

  return createElement(
    'div',
    { className: 'editable-cell-lookup' },
    createElement(CustomDropdown, {
      options: Object.entries(lookupMap).map(([id, label]) => ({ value: id, label })),
      value: String(value ?? '') || undefined,
      selectedLabelOverride: displayLookupMap?.[String(value ?? '')],
      isInvalid: isInvalidLookupValue,
      onChange: (nextValue: string | number | undefined) => onValueChange(nextValue ?? ''),
      searchable: true,
      showAllOption: false,
      placeholder: '',
      triggerIcon: createElement(ArrowDropDown, { sx: { fontSize: 20, flexShrink: 0 } }),
    })
  );
};

const lookupGridRenderer: GridEditCellTemplateRenderer = (context) => {
  if (!hasLookupGridConfiguration(context)) return null;

  return createElement(LookupGridDropdown, {
    value: context.value,
    lookupMap: context.displayLookupMap ?? context.lookupMap ?? {},
    columnMetadata: context.columnMetadata,
    nodeKey: context.nodeKey,
    fieldKey: context.fieldKey,
    baseFilter: context.baseFilter,
    parentId: context.parentId,
    onChange: context.onValueChange,
  });
};

const colorRenderer: GridEditCellTemplateRenderer = ({ value, onValueChange }) =>
  createElement('input', {
    'aria-label': 'Select color',
    'data-grid-color-editor': true,
    type: 'color',
    value: normalizeColor(value),
    onChange: (event: ChangeEvent<HTMLInputElement>) => onValueChange(event.target.value),
    style: {
      width: '100%',
      height: '30px',
      border: 'none',
      background: 'none',
      cursor: 'pointer',
    },
  });

const fileRenderer: GridEditCellTemplateRenderer = ({ value, columnMetadata, onValueChange }) =>
  createElement(FileCell, {
    fileGuid: value != null && String(value) !== '' ? String(value) : null,
    isEditing: true,
    metadata: columnMetadata,
    onValueChange,
  });

const bankTransactionIncomeExpenseTypeRenderer: GridEditCellTemplateRenderer = ({
  value,
  onValueChange,
  onRowValueChange,
}) =>
  createElement(BankTransactionIncomeExpenseTypeEditCell, {
    value,
    onValueChange,
    onRowValueChange,
  });

const bankTransactionDependentLookupRenderer: GridEditCellTemplateRenderer = (context) => {
  if (!hasLookupGridConfiguration(context)) return null;

  const dependencyValue = context.rowData?.IncomeExpenseType;

  return createElement(LookupGridDropdown, {
    value: context.value,
    lookupMap: context.displayLookupMap ?? context.lookupMap ?? {},
    columnMetadata: context.columnMetadata,
    nodeKey: context.nodeKey,
    fieldKey: context.fieldKey,
    baseFilter: context.baseFilter,
    parentId: context.parentId,
    selectedEntity:
      dependencyValue === null || dependencyValue === undefined || dependencyValue === ''
        ? undefined
        : String(dependencyValue),
    onChange: context.onValueChange,
  });
};

const gridEditCellTemplateRegistry: GridEditCellTemplateRegistry = new Map([
  ['banktransactionincomeexpensetype', bankTransactionIncomeExpenseTypeRenderer],
  ['banktransactiondependentlookup', bankTransactionDependentLookupRenderer],
  ['selectlookup', selectLookupRenderer],
  ['selectgrid', lookupGridRenderer],
  ['selectaddress', lookupGridRenderer],
  ['color', colorRenderer],
  ['dxcolorbox', colorRenderer],
  ['file', fileRenderer],
  ['customfileuploader', fileRenderer],
  ['dxfileuploader', fileRenderer],
]);

export const resolveGridEditCellTemplate = (
  editor: string | null | undefined,
  dataType: string | null | undefined,
  registry: GridEditCellTemplateRegistry = gridEditCellTemplateRegistry
): GridEditCellTemplateRenderer | null => {
  const editorKey = editor?.trim().toLowerCase();
  const dataTypeKey = dataType?.trim().toLowerCase();

  return (
    (editorKey ? registry.get(editorKey) : undefined) ??
    (dataTypeKey ? registry.get(dataTypeKey) : undefined) ??
    null
  );
};

export const renderGridEditCellTemplate = (
  context: GridEditCellTemplateContext,
  registry: GridEditCellTemplateRegistry = gridEditCellTemplateRegistry
): ReactElement | null => {
  const cellTemplateRenderer = resolveGridEditCellTemplate(
    context.columnMetadata.cellTemplateFunction,
    undefined,
    registry
  );
  const renderer =
    cellTemplateRenderer ??
    resolveGridEditCellTemplate(
      context.columnMetadata.editor,
      context.columnMetadata.type ?? context.columnMetadata.dataType,
      registry
    );

  if (!renderer) return null;

  try {
    return renderer(context);
  } catch {
    return null;
  }
};
